import React, { useState, useEffect } from 'react';
import styles from './BqoolConsentToggle.module.css';
import { useUserContext } from '@/contexts/UserContext';
import { updateGoAuraIntegration } from '@/api/settings';
import { useErrorModalContext } from '@/contexts/ErrorModalContext';

const BqoolConsentToggle = () => {
  const { user, setUser } = useUserContext();
  const [isActive, setIsActive] = useState(user?.bqoolIntegration || false);
  const [isLoading, setIsLoading] = useState(false);
  const { setErrors } = useErrorModalContext();

  // Sync component state with user data when it changes
  useEffect(() => {
    if (user) {
      setIsActive(user.bqoolIntegration || false);
    }
  }, [user]);

  const handleToggle = async () => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      const newStatus = !isActive;
      
      // Optimistically update UI
      setIsActive(newStatus);
      
      await updateGoAuraIntegration({ bqoolIntegration: newStatus });
      setUser({ ...user, bqoolIntegration: newStatus }, false);
    } catch (error) {
      // Revert UI state if API call fails
      setIsActive(isActive);
      console.error('Error updating BQool integration:', error);
      setErrors([{ 
        message: "Failed to update BQool integration. Please try again later."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logoSection}>
        <div className={styles.logoContainer}>
          <svg width="138" height="60" viewBox="0 0 138 60" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.logo}>
            <g clipPath="url(#clip0_2125_11)">
              <path d="M61.0406 34.0792C61.0406 34.17 61.0406 34.2538 61.0406 34.3446V34.4738C61.0266 34.9593 60.6599 35.3609 60.1745 35.4168C60.1361 35.4238 60.1011 35.4237 60.0697 35.4237C59.6262 35.4237 59.242 35.1304 59.1233 34.6938C57.0767 26.8498 51.7717 22.3516 44.5563 22.3516C40.9626 22.3516 37.2537 24.0279 34.6483 26.8289C34.4737 27.0244 34.2222 27.1362 33.9533 27.1362C33.6879 27.1467 33.4329 27.0419 33.2478 26.8603L29.5598 23.1758C29.1826 22.7951 29.1791 22.1839 29.5459 21.8033C32.2001 19.0757 35.0429 17.3085 38.2211 16.4005C37.0511 11.9197 34.093 9.08033 29.2141 7.73225C28.1524 7.44237 27.0697 7.2922 25.994 7.2922C22.3899 7.2922 19.0651 8.97206 16.9102 11.8359C18.2548 12.6775 19.3968 13.7637 20.256 15C20.5249 15.3807 20.476 15.9045 20.1442 16.2398L17.1268 19.2608C16.9137 19.4668 16.6204 19.5751 16.32 19.5402C16.0162 19.4913 15.7507 19.3236 15.6006 19.0652C13.6518 15.7404 10.8613 14.057 7.31652 14.057C4.11046 14.057 1.46669 15.447 1.43875 15.4505C1.02665 15.6775 0.50278 15.5623 0.216401 15.1956C-0.0734712 14.8219 -0.0490242 14.2875 0.27228 13.9383C2.67158 11.3225 5.99638 9.86263 9.64597 9.82072C12.4574 3.83469 18.4679 0 25.1244 0C26.3188 0 27.5167 0.12922 28.6937 0.37369C36.1745 1.96275 41.6052 8.20023 42.2059 15.7334C42.6041 15.7055 42.9987 15.695 43.3933 15.695C52.9591 15.695 61.0406 24.1153 61.0406 34.0757V34.0792ZM137.927 49.7462C135.534 49.7462 133.596 47.8114 133.596 45.4226L133.505 31.5227V24.5797H123.307V45.0664H123.353V45.1327C123.353 45.227 123.346 45.3248 123.346 45.4226C123.346 49.4424 124.98 53.0885 127.62 55.7253C130.257 58.3586 133.9 60 137.93 60H137.986V49.7462H137.93H137.927ZM121.785 45.2829C121.785 53.3749 115.229 59.9371 107.134 59.9371C99.0383 59.9371 92.4795 53.3749 92.4795 45.2829C92.4795 37.1909 99.0383 30.6251 107.134 30.6251C115.229 30.6251 121.785 37.1839 121.785 45.2829ZM111.656 45.2829C111.656 42.7823 109.631 40.7602 107.134 40.7602C104.637 40.7602 102.615 42.7823 102.615 45.2829C102.615 47.7835 104.637 49.7986 107.134 49.7986C109.631 49.7986 111.656 47.7765 111.656 45.2829ZM91.0511 45.2829C91.0511 53.3749 84.4888 59.9371 76.3968 59.9371C68.3049 59.9371 61.7426 53.3749 61.7426 45.2829C61.7426 37.1909 68.3014 30.6251 76.3968 30.6251C84.4923 30.6251 91.0511 37.1839 91.0511 45.2829ZM80.9126 45.2829C80.9126 42.7823 78.8904 40.7602 76.3968 40.7602C73.9032 40.7602 71.8776 42.7823 71.8776 45.2829C71.8776 47.7835 73.9032 49.7986 76.3968 49.7986C78.8904 49.7986 80.9126 47.7765 80.9126 45.2829ZM60.2199 45.2829C60.2199 48.4086 59.2315 51.3073 57.5587 53.6892L59.9824 59.9965L53.3747 57.6636C51.1116 59.0955 48.4399 59.9336 45.5691 59.9336C37.4737 59.9336 30.9114 53.3714 30.9114 45.2794C30.9114 37.1874 37.4737 30.6217 45.5691 30.6217C53.6646 30.6217 60.2199 37.1804 60.2199 45.2794V45.2829ZM50.0849 45.2829C50.0849 42.7823 48.0557 40.7602 45.5656 40.7602C43.0755 40.7602 41.0429 42.7823 41.0429 45.2829C41.0429 47.7835 43.0686 49.7986 45.5656 49.7986C48.0627 49.7986 50.0849 47.7765 50.0849 45.2829ZM29.4795 45.2829C29.4795 53.3749 22.9172 59.9371 14.8252 59.9371C6.73328 59.9371 0.170999 53.3749 0.170999 45.2829C0.170999 45.1851 0.184969 45.0978 0.188461 45.014H0.170999V24.5797H10.3759V31.3201C11.7798 30.8696 13.2711 30.6286 14.8252 30.6286C22.9207 30.6286 29.4795 37.1874 29.4795 45.2864V45.2829ZM19.3479 45.2829C19.3479 42.7823 17.3258 40.7602 14.8252 40.7602C12.3247 40.7602 10.306 42.7823 10.306 45.2829C10.306 47.7835 12.3282 49.7986 14.8252 49.7986C17.3223 49.7986 19.3479 47.7765 19.3479 45.2829Z" fill="#0065A8"/>
            </g>
            <defs>
              <clipPath id="clip0_2125_11">
                <rect width="138" height="60" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </div>
        <div className={`${styles.statusBadge} ${isActive ? styles.statusActive : ''}`}>
          {isActive ? 'Active' : 'Not Active'}
        </div>
      </div>

      <div className={styles.contentSection}>
        <div className={styles.consentText}>
          I Allow the integration of my Boxem account with BQool.
        </div>
        <div className={styles.dataSection}>
          <div className={styles.dataLabel}>Data Transferred:</div>
          <div className={styles.dataItem}>
            <div className={styles.dataDot}></div>
            <a href="#" className={styles.dataLink}>Buy Cost Data</a>
          </div>
        </div>
      </div>

      <div className={styles.toggleSection}>
        <div className={styles.toggleLabel}>Allow Integration</div>
        <button 
            onClick={handleToggle}
            disabled={isLoading}
            className={`${styles.toggleButton} ${isActive ? styles.toggleActive : ''}`}
            aria-label={isActive ? 'Disable BQool integration' : 'Enable BQool integration'}
            aria-busy={isLoading}
        >
            <span className={`${styles.toggleThumb} ${isActive ? styles.toggleThumbActive : ''}`} />
        </button>
        </div>
    </div>
  );
};

export default BqoolConsentToggle;